import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { intlLocale, pageLang, t as tr, type UiString } from "../i18n/ui";
import { clamp, type Point, type Sample, sampleCurve, streamPath } from "./math";

interface Stream {
  points: Point[];
}
interface Layout {
  height: number;
  streams: Stream[];
  captions?: { text: UiString; x: number; y: number }[];
  readout?: { x: number; y: number };
  silhouette?: boolean;
  through?: { to: string; keep: number };
}

const BUILDING = { left: 0.12, top: 0.12, size: 0.74, below: 0.955 };

const W = 1920;
const NS = "http://www.w3.org/2000/svg";
const STRANDS = 5;
const SPACING = 13;
const WAVELENGTH = 300;
const BASE_FLOW = 2.4;
const flowNumber = new Intl.NumberFormat(intlLocale(pageLang()), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const flowText = (pace: number) => `${flowNumber.format(BASE_FLOW * pace)} m³/h`;

const LAYOUTS: Record<string, Layout> = {
  built: {
    height: 1300,
    streams: [
      {
        points: [
          [2080, 170],
          [1700, 200],
          [1540, 330],
          [1250, 300],
          [960, 360],
          [800, 560],
          [640, 820],
          [420, 990],
          [120, 1060],
          [-180, 1080],
        ],
      },
    ],
    readout: { x: 770, y: 400 },
    silhouette: true,
    through: { to: ".sides__image", keep: 6 },
  },
  networksTop: {
    height: 900,
    streams: [
      {
        points: [
          [-140, 70],
          [260, 120],
          [430, 300],
          [800, 260],
          [1000, 520],
          [1320, 720],
          [2080, 700],
        ],
      },
    ],
  },
  networksBottom: {
    height: 820,
    streams: [
      {
        points: [
          [2080, 130],
          [1500, 160],
          [1050, 300],
          [800, 560],
          [400, 600],
          [120, 690],
          [-160, 700],
        ],
      },
    ],
  },
  cta: {
    height: 1190,
    streams: [
      {
        points: [
          [2120, 1080],
          [1500, 1000],
          [1240, 820],
          [760, 800],
          [460, 700],
          [-140, 560],
        ],
      },
      {
        points: [
          [-140, 240],
          [320, 260],
          [620, 480],
          [1240, 600],
          [1540, 700],
          [2120, 860],
        ],
      },
    ],
    captions: [
      { text: "TOTAL VOLUME", x: 330, y: 215 },
      { text: "AVERAGE FLOW", x: 760, y: 585 },
    ],
  },
};

let uid = 0;
const el = <K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {},
) => {
  const node = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
};

interface Built {
  strands: { path: SVGPathElement; samples: Sample[]; index: number }[];
  glint: SVGLinearGradientElement[];
  captions: SVGTextElement[];
  readout: SVGTextElement | null;
  flow: SVGPathElement[];
  svg: SVGSVGElement;
  pieces: { samples: Sample[]; grad: SVGLinearGradientElement }[];
}

function build(host: HTMLElement, layout: Layout): Built {
  const frame = (cls?: string) =>
    el("svg", {
      viewBox: `0 0 ${W} ${layout.height}`,
      fill: "none",
      preserveAspectRatio: "xMidYMid meet",
      ...(cls ? { class: cls } : {}),
    });
  const svg = frame();
  const defs = el("defs");
  svg.appendChild(defs);
  const solid = Boolean(layout.silhouette);
  const edge = "var(--stream-edge)";
  const core = "var(--stream-core)";
  const softSvg = solid ? frame("streams__soft") : null;
  const built: Built = {
    strands: [],
    glint: [],
    captions: [],
    readout: null,
    flow: [],
    svg: svg as SVGSVGElement,
    pieces: [],
  };
  const bands = softSvg ?? svg;
  const hazeSvg = layout.through ? frame("streams__haze") : null;

  const gradient = (
    stops: readonly (readonly [number, string, number])[],
    from: Point,
    to: Point,
  ) => {
    const g = el("linearGradient", {
      id: `stream-${++uid}`,
      gradientUnits: "userSpaceOnUse",
      x1: from[0],
      y1: from[1],
      x2: to[0],
      y2: to[1],
    });
    for (const [o, c, a] of stops) {
      const stop = el("stop", { offset: o, "stop-opacity": a });
      stop.style.stopColor = c;
      g.appendChild(stop);
    }
    defs.appendChild(g);
    return g;
  };
  const ribbon = (ends: [number, number]) =>
    [
      [0, edge, 0],
      [ends[0], edge, 0.75],
      [0.5, core, 0.9],
      [1 - ends[1], edge, 0.75],
      [1, edge, 0],
    ] as const;

  const strands = (target: SVGElement, samples: Sample[], grad: string, wide: boolean) => {
    for (let i = 0; i < STRANDS; i++) {
      const centre = i === (STRANDS - 1) / 2;
      const nearness = 1 - Math.abs(i - (STRANDS - 1) / 2) / ((STRANDS - 1) / 2);
      const width = wide ? 12 + 14 * nearness : solid ? 8 + 10 * nearness : centre ? 1.6 : 1;
      const alpha = wide
        ? 0.3 + 0.35 * nearness
        : solid
          ? 0.28 + 0.42 * nearness
          : centre
            ? 1
            : 0.35 + 0.5 * nearness;
      const path = el("path", {
        stroke: `url(#${grad})`,
        "stroke-width": width,
        "stroke-linecap": "round",
        "vector-effect": "non-scaling-stroke",
        opacity: alpha,
      });
      target.appendChild(path);
      built.strands.push({ path, samples, index: i });
    }
  };
  const glintAndDrops = (samples: Sample[], glow: string) => {
    const glintPath = el("path", {
      stroke: `url(#${glow})`,
      "stroke-width": 2.2,
      "stroke-linecap": "round",
      "vector-effect": "non-scaling-stroke",
    });
    svg.appendChild(glintPath);
    built.strands.push({ path: glintPath, samples, index: (STRANDS - 1) / 2 });
    if (!solid) return;
    const flowPath = el("path", {
      stroke: "#84C3BE",
      "stroke-width": 3,
      "stroke-linecap": "round",
      "stroke-dasharray": "0.1 38",
      "vector-effect": "non-scaling-stroke",
      opacity: 0.9,
    });
    flowPath.style.stroke = "var(--stream-gap)";
    svg.appendChild(flowPath);
    built.strands.push({ path: flowPath, samples, index: (STRANDS - 1) / 2 });
    built.flow.push(flowPath);
  };

  layout.streams.forEach((stream) => {
    const first = stream.points[0] as Point;
    const last = stream.points[stream.points.length - 1] as Point;
    const shine = solid ? "var(--stream-gap)" : "var(--stream-shine)";
    const g2 = gradient(
      [
        [0, shine, 0],
        [0.42, shine, 0],
        [0.5, shine, 1],
        [0.58, shine, 0],
        [1, shine, 0],
      ],
      first,
      last,
    );
    g2.setAttribute("spreadMethod", "pad");
    built.glint.push(g2);
    const samples = sampleCurve(stream.points, 28);

    if (layout.through && hazeSvg) {
      const above = { samples: [...samples], grad: gradient(ribbon([0.18, 0.04]), first, last) };
      const inside = { samples: [] as Sample[], grad: gradient(ribbon([0.12, 0.12]), first, last) };
      const below = { samples: [] as Sample[], grad: gradient(ribbon([0.05, 0.18]), first, last) };
      built.pieces.push(above, inside, below);
      strands(bands, above.samples, above.grad.id, false);
      strands(bands, below.samples, below.grad.id, false);
      strands(hazeSvg, inside.samples, inside.grad.id, true);
      glintAndDrops(above.samples, g2.id);
      glintAndDrops(below.samples, g2.id);
      return;
    }
    const g1 = gradient(ribbon([0.18, 0.18]), first, last);
    strands(bands, samples, g1.id, false);
    glintAndDrops(samples, g2.id);
  });

  for (const c of layout.captions ?? []) {
    const t = el("text", { x: c.x, y: c.y, class: "cta-cap" });
    t.textContent = tr(c.text);
    built.captions.push(svg.appendChild(t));
  }
  if (layout.readout) {
    const { x, y } = layout.readout;
    const label = el("text", { x, y, class: "flow-readout flow-readout__label" });
    label.textContent = tr("FLOW");
    const value = el("text", { x, y: y + 32, class: "flow-readout flow-readout__value" });
    value.textContent = flowText(1);
    svg.append(label, value);
    built.readout = value;
  }
  host.append(...[hazeSvg, softSvg, svg].filter((x): x is SVGSVGElement => x !== null));
  return built;
}

const touch = window.matchMedia("(pointer: coarse)").matches;
let scrolledAt = -Infinity;
if (touch) {
  window.addEventListener("scroll", () => (scrolledAt = performance.now()), { passive: true });
}

function shape(built: Built, time: number, scroll: number): void {
  if (touch) scroll = 0;
  for (const s of built.strands) {
    const offset = (s.index - (STRANDS - 1) / 2) * SPACING;
    const amplitude = 9 + 5 * Math.sin(s.index * 1.7);
    const phase = time * (0.9 + s.index * 0.12) + s.index * 0.8 + scroll * Math.PI * 4;
    s.path.setAttribute(
      "d",
      streamPath(s.samples, offset, amplitude, WAVELENGTH + s.index * 24, phase),
    );
  }
}

function moveGlint(built: Built, progress: number): void {
  const t = clamp(progress) * 1.6 - 0.8;
  for (const g of built.glint) {
    const x1 = Number(g.getAttribute("x1"));
    const x2 = Number(g.getAttribute("x2"));
    const y1 = Number(g.getAttribute("y1"));
    const y2 = Number(g.getAttribute("y2"));
    g.setAttribute("gradientTransform", `translate(${(x2 - x1) * t} ${(y2 - y1) * t})`);
  }
}

export function initStreams(reduced: boolean): void {
  document.querySelectorAll<HTMLElement>("[data-streams]").forEach((host) => {
    const layout = LAYOUTS[host.dataset.streams ?? ""];
    if (!layout) return;
    const draw = host.dataset.streamsMode === "draw";
    const built = build(host, layout);
    let time = 0;
    let scroll = 0;
    if (layout.through) {
      const { to, keep } = layout.through;
      const target = document.querySelector<HTMLElement>(to);
      const route = () => {
        const box = target?.getBoundingClientRect();
        const frame = built.svg.getBoundingClientRect();
        const first = layout.streams[0];
        if (!box || !frame.width || !first || built.pieces.length < 3) return;
        const k = W / frame.width;
        const size = box.width * BUILDING.size * k;
        const left = (box.left - frame.left + box.width * BUILDING.left) * k;
        const top = (box.top - frame.top + box.height * BUILDING.top) * k;
        const x = left + size / 2;
        const bottom = top + size;
        const under = (box.top - frame.top + box.height * BUILDING.below) * k;
        const from = first.points[keep - 1] ?? [x, top - 600];
        const narrow = frame.width > window.innerWidth * 1.2;
        const seen0 = (0 - frame.left) * k;
        const seen = window.innerWidth * k;
        const at = (fx: number) => seen0 + seen * fx;
        const fall = (fy: number) => 520 + (top - size * 0.12 - 520) * fy;
        const approach: Point[] = narrow
          ? [
              [at(1) + 260, 120],
              [at(0.78), 300],
              [at(0.52), fall(0.12)],
              [at(0.3), fall(0.36)],
              [at(0.66), fall(0.62)],
              [(at(0.6) + x) / 2, fall(0.86)],
            ]
          : [...first.points.slice(0, keep), [(from[0] + x) / 2 + 40, (from[1] + top) / 2]];
        const points: Point[] = [
          ...approach,
          [x, top - size * 0.12],
          [x - size * 0.04, top + size * 0.35],
          [x + size * 0.04, top + size * 0.68],
          [x, bottom + size * 0.02],
          [x - size * 0.12, under],
          [x - size * 0.6, under + 10],
          [-240, under + 14],
        ];
        const all = sampleCurve(points, 28);
        const into = all.findIndex((p) => p.y >= top + size * 0.03);
        const outOf = all.findIndex((p, i) => i > into && p.y >= bottom - size * 0.03);
        const cut = (from: number, to: number) => all.slice(Math.max(0, from), Math.max(0, to));
        const parts = [cut(0, into + 3), cut(into - 4, outOf + 4), cut(outOf - 3, all.length)];
        built.pieces.forEach((piece, i) => {
          const part = parts[i] ?? [];
          piece.samples.splice(0, piece.samples.length, ...part);
          const a0 = part[0];
          const a1 = part[part.length - 1];
          if (!a0 || !a1) return;
          piece.grad.setAttribute("x1", a0.x.toFixed(1));
          piece.grad.setAttribute("y1", a0.y.toFixed(1));
          piece.grad.setAttribute("x2", a1.x.toFixed(1));
          piece.grad.setAttribute("y2", a1.y.toFixed(1));
        });
        const end = all[all.length - 1];
        for (const g of built.glint) {
          if (end) g.setAttribute("x2", end.x.toFixed(1));
          if (end) g.setAttribute("y2", end.y.toFixed(1));
        }
        shape(built, time, scroll);
      };
      route();
      window.addEventListener("load", route);
      void document.fonts?.ready.then(route);
      if (target) new ResizeObserver(route).observe(document.body);
    }
    shape(built, 0, 0);
    moveGlint(built, 0.5);

    ScrollTrigger.create({
      trigger: host,
      start: "top bottom",
      end: "bottom top",
      onUpdate: (self) => {
        scroll = self.progress;
        moveGlint(built, self.progress);
        if (reduced) shape(built, time, scroll);
      },
    });

    if (draw) {
      const paths = built.strands.map((s) => s.path);
      if (reduced) {
        gsap.set(built.captions, { opacity: 1 });
      } else {
        paths.forEach((p) => {
          const len = p.getTotalLength();
          p.style.strokeDasharray = `${len}`;
          p.style.strokeDashoffset = `${len}`;
        });
        ScrollTrigger.create({
          trigger: host,
          start: "top 85%",
          once: true,
          onEnter: () => {
            gsap.to(paths, {
              strokeDashoffset: 0,
              duration: 2.6,
              ease: "power2.inOut",
              stagger: 0.05,
              onComplete: () => paths.forEach((p) => (p.style.strokeDasharray = "none")),
            });
            gsap.to(built.captions, {
              opacity: 1,
              duration: 0.55,
              stagger: 0.1,
              ease: "power2.out",
              delay: 1.6,
            });
          },
        });
      }
    }
    if (reduced) return;

    let clock = 0;
    let along = 0;
    let shown = 0;
    const pace = () => 1 + 0.45 * Math.sin(clock * 0.35) + 0.12 * Math.sin(clock * 1.1 + 0.8);
    const showFlow = (now: number) => {
      if (!built.readout || now - shown < 120) return;
      shown = now;
      built.readout.textContent = flowText(pace());
    };

    let visible = false;
    let last = 0;
    let shaped = 0;
    let pending = 0;
    const tick = (now: number) => {
      if (!visible) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      clock += dt;
      const p = built.readout ? pace() : 1;
      along += dt * 150 * p;
      for (const f of built.flow) f.style.strokeDashoffset = `${-along}`;
      if (touch && now - scrolledAt < 200) pending = 0;
      else pending += dt * p;
      if (!touch || (pending > 0 && now - shaped >= 33)) {
        time += pending;
        pending = 0;
        shaped = now;
        shape(built, time, scroll);
      }
      showFlow(now);
      requestAnimationFrame(tick);
    };
    new IntersectionObserver(([entry]) => {
      const was = visible;
      visible = Boolean(entry?.isIntersecting);
      if (visible && !was) {
        last = performance.now();
        requestAnimationFrame(tick);
      }
    }).observe(host);
  });
}
